import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Box,
  Chip,
  alpha,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import {
  inputRootSx,
  autocompleteChipSx,
  FORM_NEON,
  FORM_SURFACE_BASE,
  FORM_SURFACE_MID,
  FORM_TEXT_BODY,
} from './formFieldSx';

const menuPaperSx = {
  background: `linear-gradient(160deg, ${alpha(FORM_SURFACE_BASE, 0.98)} 0%, ${alpha(FORM_SURFACE_MID, 0.96)} 100%)`,
  border: `1px solid ${alpha(FORM_NEON, 0.2)}`,
  boxShadow: `0 8px 32px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(FORM_NEON, 0.1)}`,
  backdropFilter: 'blur(12px)',
  color: alpha(FORM_TEXT_BODY, 0.85),
  '& .MuiAutocomplete-listbox': {
    py: 0.5,
  },
  '& .MuiAutocomplete-option': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    color: alpha(FORM_TEXT_BODY, 0.85),
    borderRadius: '8px',
    margin: '2px 8px',
    minHeight: '44px !important',
    padding: '10px 12px !important',
    transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      background: alpha(FORM_NEON, 0.1),
      color: alpha(FORM_TEXT_BODY, 0.95),
    },
    '&.Mui-focused:not(.Mui-selected)': {
      background: alpha(FORM_NEON, 0.14),
      color: alpha(FORM_TEXT_BODY, 0.95),
    },
    '&.Mui-selected, &[aria-selected="true"].Mui-selected': {
      background: `${alpha(FORM_NEON, 0.24)} !important`,
      color: `${FORM_NEON} !important`,
      fontWeight: 600,
      boxShadow: `inset 3px 0 0 ${FORM_NEON}`,
    },
    '&.Mui-selected:hover, &[aria-selected="true"].Mui-selected:hover': {
      background: `${alpha(FORM_NEON, 0.32)} !important`,
      color: `${FORM_NEON} !important`,
    },
    '&[aria-selected="true"]:not(.Mui-selected)': {
      background: alpha(FORM_NEON, 0.14),
      color: alpha(FORM_TEXT_BODY, 0.95),
    },
  },
  '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.875rem',
    color: `${alpha(FORM_TEXT_BODY, 0.72)} !important`,
    WebkitTextFillColor: `${alpha(FORM_TEXT_BODY, 0.72)} !important`,
    padding: '12px 14px',
  },
};

/** Sentinel values (e.g. __custom__) stay visible while filtering. */
const isPinnedOption = (opt) => String(opt?.value ?? '').startsWith('__');

/**
 * Type-to-search select. Options: { value, label }.
 * Set `multiple` for multi-select (value/onChange use arrays).
 */
const SearchableSelect = ({
  label,
  value,
  onChange,
  options = [],
  multiple = false,
  required = false,
  disabled = false,
  error = false,
  helperText,
  startIcon = null,
  placeholder = '',
  labelAlwaysShrink = false,
  limitTags = -1,
  stackedTags = false,
  sx = {},
  size = 'medium',
}) => {
  const selected = useMemo(() => {
    if (multiple) {
      const vals = new Set((Array.isArray(value) ? value : []).map((v) => String(v)));
      return options.filter((o) => vals.has(String(o.value)));
    }
    return options.find((o) => String(o.value) === String(value)) ?? null;
  }, [multiple, options, value]);

  const selectedLabel = multiple ? '' : (selected?.label ?? '');
  const [inputValue, setInputValue] = useState(selectedLabel);

  // Keep the text field in sync when the value changes from outside (e.g. form load).
  useEffect(() => {
    if (!multiple) {
      setInputValue(selectedLabel);
    }
  }, [multiple, selectedLabel, value]);

  // Native `required` on multi Autocomplete targets the empty search input, not selected chips.
  const nativeRequired = required && !multiple;
  const hasSelection = multiple
    ? (Array.isArray(selected) && selected.length > 0)
    : Boolean(selected);

  return (
    <Autocomplete
      fullWidth
      multiple={multiple}
      disabled={disabled}
      options={options}
      value={multiple ? selected : selected}
      inputValue={multiple ? undefined : inputValue}
      onInputChange={
        multiple
          ? undefined
          : (_, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
              } else if (reason === 'clear') {
                setInputValue('');
              } else if (reason === 'reset') {
                setInputValue(selectedLabel);
              }
            }
      }
      clearOnBlur={false}
      disableClearable={required && !multiple}
      onOpen={
        multiple
          ? undefined
          : () => {
              // Clear search text so the full option list is visible (not filtered by the selected label).
              setInputValue('');
            }
      }
      onClose={
        multiple
          ? undefined
          : () => {
              setInputValue(selectedLabel);
            }
      }
      onChange={(_, opt, reason) => {
        if (multiple) {
          onChange((opt || []).map((o) => o.value));
          return;
        }
        // Ignore null emissions while the user is typing — only commit on pick or clear.
        if (reason === 'selectOption') {
          onChange(opt?.value ?? '');
          setInputValue(opt?.label ?? '');
          return;
        }
        if (reason === 'clear') {
          onChange('');
          setInputValue('');
        }
      }}
      getOptionLabel={(opt) => opt?.label ?? ''}
      isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
      autoHighlight
      openOnFocus
      disableCloseOnSelect={multiple}
      limitTags={multiple && limitTags >= 0 ? limitTags : undefined}
      renderTags={multiple && stackedTags ? (tagValue, getTagProps) => (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          flex: '1 0 calc(100% - 48px)',
          width: 'calc(100% - 48px)',
          minWidth: 0,
          mr: 0.5,
        }}>
          {tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...tagProps}
                label={option.label}
                size="small"
                variant="outlined"
                sx={{
                  ...autocompleteChipSx,
                  width: '100%',
                  maxWidth: 'none',
                  position: 'relative',
                  justifyContent: 'center',
                  '& .MuiChip-label': {
                    width: '100%',
                    px: 4,
                    textAlign: 'center',
                  },
                  '& .MuiChip-deleteIcon': {
                    position: 'absolute',
                    right: 8,
                  },
                }}
              />
            );
          })}
        </Box>
      ) : undefined}
      filterOptions={(opts, { inputValue: filterInput }) => {
        const q = (filterInput || '').trim().toLowerCase();
        if (!q) return opts;
        return opts.filter(
          (o) => isPinnedOption(o) || (o.label || '').toLowerCase().includes(q),
        );
      }}
      noOptionsText="No matches"
      renderOption={(props, option, state) => {
        const { key, ...optionProps } = props;
        const selected = state.selected;
        return (
          <Box
            component="li"
            key={key}
            {...optionProps}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              width: '100%',
            }}
          >
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              {option.label}
            </Box>
            {selected ? (
              <CheckIcon
                aria-hidden
                sx={{ fontSize: 18, color: FORM_NEON, flexShrink: 0, opacity: 0.95 }}
              />
            ) : null}
          </Box>
        );
      }}
      sx={multiple ? { '& .MuiChip-root': autocompleteChipSx } : undefined}
      slotProps={{
        paper: { sx: menuPaperSx },
        listbox: { sx: { color: alpha(FORM_TEXT_BODY, 0.85) } },
        chip: multiple ? { sx: autocompleteChipSx, size: 'small', variant: 'outlined' } : undefined,
        noOptions: {
          sx: {
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.875rem',
            color: `${alpha(FORM_TEXT_BODY, 0.72)} !important`,
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={nativeRequired}
          error={error}
          helperText={helperText}
          placeholder={
            multiple
              ? (hasSelection ? undefined : placeholder)
              : (selected ? undefined : placeholder)
          }
          size={size}
          InputLabelProps={{
            ...params.InputLabelProps,
            required,
            shrink: labelAlwaysShrink ? true : params.InputLabelProps?.shrink,
          }}
          inputProps={{
            ...params.inputProps,
            required: nativeRequired || undefined,
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {startIcon ? (
                  <InputAdornment position="start">{startIcon}</InputAdornment>
                ) : null}
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          FormHelperTextProps={{
            sx: {
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.68rem',
              letterSpacing: 0.3,
              color: error ? '#F87171 !important' : `${alpha(FORM_TEXT_BODY, 0.78)} !important`,
              WebkitTextFillColor: error ? '#F87171 !important' : `${alpha(FORM_TEXT_BODY, 0.78)} !important`,
              opacity: '1 !important',
              marginTop: 3,
              marginLeft: 0,
            },
          }}
          sx={{ ...inputRootSx, ...sx }}
        />
      )}
    />
  );
};

export default SearchableSelect;
